import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import DataTable, { Column } from "@/components/layouts/DataTable";
import DeleteConfirmationDialog from "@/components/layouts/DeleteConfirmationDialog";
import FilterOptionsButton from "@/components/layouts/FilterOptionsButton";
import PageHeader from "@/components/layouts/PageHeader";
import SearchInput from "@/components/layouts/SearchInput";
import PageLoader from "@/components/loader/PageLoader";
import { Button } from "@/components/ui/button";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faEdit,
  faSearch,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DailySpecialModal from "./DailySpecialModal";

interface ProductItem {
  id: number;
  name: string;
  menu_display_name: string;
  image: string;
  category: number;
  unit_price: string;
  in_stock: boolean;
  is_available: boolean;
  status: string;
  created_at: string;
  category_name: string;
  product_type: string;
}

interface SortState {
  field: keyof ProductItem;
  direction: "asc" | "desc";
}

interface FilterState {
  status: string;
  category: string;
  priceGt: string;
  priceLt: string;
  productType: string;
}

const Product = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [productItems, setProductItems] = useState<ProductItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    field: "name",
    direction: "asc",
  });
  const { user } = useUser();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [dailySpecialModalOpen, setDailySpecialModalOpen] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>({
    status: "",
    category: "",
    priceGt: "",
    priceLt: "",
    productType: "",
  });

  const columns: Column<ProductItem>[] = [
    {
      key: "image",
      header: "",
      render: (item: ProductItem) =>
        item.image ? (
          <img src={item.image} alt={item.name} className="h-16 w-16" />
        ) : null,
    },
    {
      key: "menu_display_name",
      header: t("back.management.menu.product.table.menuDisplayName"),
      sortable: true,
      bold: true,
    },
    {
      key: "category_name",
      header: t("back.management.menu.product.table.category"),
      sortable: true,
    },
    {
      key: "product_type",
      header: t("back.management.menu.product.table.productType"),
      sortable: true,
    },
    {
      key: "unit_price",
      header: t("back.management.menu.product.table.price"),
      sortable: true,
      bold: true,
    },
    {
      key: "status",
      header: t("back.management.menu.product.table.status"),
      bold: true,
    },
    {
      key: "created_at",
      header: t("back.management.menu.product.table.date"),
      sortable: true,
    },
    {
      key: "action",
      header: t("back.management.menu.product.table.action"),
    },
  ];

  const getStatusColor = (item: ProductItem) => {
    if (item.product_type === "Service") {
      return item.is_available ? "bg-green-500" : "bg-red-500";
    } else if (item.product_type === "Physical Product") {
      return item.in_stock ? "bg-green-500" : "bg-red-500";
    } else {
      return "bg-gray-500";
    }
  };

  const renderMobileCard = (item: ProductItem) => (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center">
        {item.image && (
          <img src={item.image} alt={item.name} className="mr-4 h-16 w-16" />
        )}
        <div>
          <p className="font-bold text-gray-900">{item.menu_display_name}</p>
          <p className="text-gray-700">{item.category_name}</p>
          <p className="font-bold text-gray-900">${item.unit_price}</p>
        </div>
      </div>
      <div className="flex h-full flex-col justify-between">
        <Link
          to={`/menu/products/edit/${item.id}`}
          className="fa-2x mb-2 text-gray-600 hover:text-gray-800"
        >
          <FontAwesomeIcon icon={faEdit} />
        </Link>
        <button
          onClick={() => openDeleteConfirmation(item.id)}
          className="fa-2x text-red-600 hover:text-red-800"
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </button>
      </div>
    </div>
  );

  const fetchProductItems = async () => {
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        const apiUrl =
          import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION ?? "";
        let url = `${apiUrl}?branch=${user.selectedBranch.id}&page=${currentPage}&page_size=${itemsPerPage}`;

        if (searchQuery) {
          url += `&menu_display_name=${encodeURIComponent(searchQuery)}`;
        }
        if (filterState.status) {
          url += `&status=${filterState.status}`;
        }
        if (filterState.category) {
          url += `&category=${encodeURIComponent(filterState.category)}`;
        }
        if (filterState.productType) {
          url += `&product_type=${encodeURIComponent(filterState.productType)}`;
        }
        if (filterState.priceGt) {
          url += `&unit_price__gt=${filterState.priceGt}`;
        }
        if (filterState.priceLt) {
          url += `&unit_price__lt=${filterState.priceLt}`;
        }

        const response = await axiosInstance.get(url);
        const fetchedItems = response.data.results.map((item: ProductItem) => {
          // Convert and format the 'created_at' date
          const zonedDate = toZonedTime(item.created_at, "America/Chicago");
          const formattedDate = format(zonedDate, "MM/dd/yyyy, HH:mm");
          return { ...item, created_at: formattedDate };
        });

        // Sorting logic
        fetchedItems.sort((a: ProductItem, b: ProductItem) => {
          if (sortState.field === "created_at") {
            // Convert back to dates for comparison
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortState.direction === "asc"
              ? dateA.getTime() - dateB.getTime()
              : dateB.getTime() - dateA.getTime();
          } else {
            const aValue = a[sortState.field];
            const bValue = b[sortState.field];
            if (typeof aValue === "number" && typeof bValue === "number") {
              return sortState.direction === "asc"
                ? aValue - bValue
                : bValue - aValue;
            } else if (
              typeof aValue === "string" &&
              typeof bValue === "string"
            ) {
              return sortState.direction === "asc"
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
            }
          }
          return 0;
        });

        setProductItems(fetchedItems);
        setTotalItems(response.data.count);
      }
    } catch (error) {
      console.error("Error fetching menu items:", error);
    }
  };

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleRemoveFilters = () => {
    setFilterState({
      status: "",
      category: "",
      priceGt: "",
      priceLt: "",
      productType: "",
    });
    setCurrentPage(1);
    fetchProductItems();
  };

  // Immediate initial fetch on component mount
  useEffect(() => {
    // Only run this on initial mount when we have the required dependencies
    if (axiosInstance !== null && axiosInstance !== undefined && user?.selectedBranch) {
      fetchProductItems();
    }
  }, [axiosInstance, user?.selectedBranch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced fetch only for search changes
  useEffect(() => {
    // Skip for initial load
    if (searchQuery === '' && currentPage === 1) {
      return;
    }
    
    const timerId = setTimeout(() => {
      fetchProductItems();
    }, 1000);

    return () => clearTimeout(timerId);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Non-debounced updates for everything else
  useEffect(() => {
    // Skip the initial render
    if (currentPage === 1 && sortState.field === "name" && sortState.direction === "asc") {
      return;
    }
    fetchProductItems();
  }, [currentPage, sortState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter state changes
  useEffect(() => {
    setCurrentPage(1);
    fetchProductItems();
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDeleteConfirmation = (id: number) => {
    setItemToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setItemToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const confirmDelete = async () => {
    if (loading || !axiosInstance || !itemToDelete) return;
    try {
      const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}${
        import.meta.env.VITE_APP_REDBIRDPOSBE_PRODUCT_INFORMATION
      }`;
      await axiosInstance.delete(`${apiUrl}${itemToDelete}`);
      fetchProductItems();
      toast.success("Product deleted successfully.");
    } catch (error) {
      console.error("Error deleting menu item:", error);
      toast.error("Failed to delete product.");
    } finally {
      closeDeleteConfirmation();
    }
  };

  const handleSortChange = (field: keyof ProductItem) => {
    setSortState((prevState) => ({
      field,
      direction:
        prevState.field === field && prevState.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const downloadCSV = () => {
    const csvRows = [
      ["ID", "Item Name", "Description", "Price"],

      ...productItems.map((item) => [
        item.id,
        item.name,
        item.menu_display_name,
        item.category,
        item.category_name,
        item.unit_price,
        item.in_stock,
        item.is_available,
        item.created_at,
        item.status,
      ]),
    ];

    const csvData = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", "product.csv");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <div className="container mx-auto px-4 py-2">
          <PageHeader
            title={t("back.management.menu.product.title")}
            exportLabel={t("back.management.menu.product.export")}
            addLabel={t("back.management.menu.product.addproduct")}
            onExport={downloadCSV}
            onAdd={() => navigate("/menu/products/add")}
          />

          <DeleteConfirmationDialog
            isOpen={deleteConfirmationOpen}
            onClose={closeDeleteConfirmation}
            onConfirm={confirmDelete}
            title={t("back.management.menu.product.deleteConfirmation.title")}
            message={t(
              "back.management.menu.product.deleteConfirmation.message",
            )}
          />

          <DailySpecialModal
            open={dailySpecialModalOpen}
            onOpenChange={setDailySpecialModalOpen}
          />

          <div className="mb-4 flex flex-col justify-between space-y-2 lg:flex-row lg:items-center lg:space-y-0">
            <div className="relative mr-4 max-w-md flex-1">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400"
              />
              <SearchInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={t("back.management.menu.product.searchProduct")}
              />
            </div>
            <div className="relative flex items-center gap-2">
              <Button
                onClick={() => setDailySpecialModalOpen(true)}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {t("back.management.menu.product.setDailySpecial")}
              </Button>

              <FilterOptionsButton
                filterOptions={[
                  {
                    label: t("back.management.menu.product.status"),
                    value: filterState.status,
                    onChange: (value) => handleFilterChange("status", value),
                    placeholder: t("back.management.menu.product.selectStatus"),
                    type: "select",
                    options: [
                      {
                        value: "In Stock",
                        label: t("back.management.menu.product.inStock"),
                      },
                      {
                        value: "Out of Stock",
                        label: t("back.management.menu.product.outOfStock"),
                      },
                      {
                        value: "Available",
                        label: t("back.management.menu.product.available"),
                      },
                      {
                        value: "Unavailable",
                        label: t("back.management.menu.product.unavailable"),
                      },
                    ],
                  },
                  {
                    label: t("back.management.menu.product.category"),
                    value: filterState.category,
                    onChange: (value) => handleFilterChange("category", value),
                    placeholder: t(
                      "back.management.menu.product.enterCategory",
                    ),
                    type: "input",
                  },
                  {
                    label: t("back.management.menu.product.priceGreaterThan"),
                    value: filterState.priceGt,
                    onChange: (value) => handleFilterChange("priceGt", value),
                    placeholder: t("back.management.menu.product.enterPrice"),
                    type: "input",
                  },
                  {
                    label: t("back.management.menu.product.priceLessThan"),
                    value: filterState.priceLt,
                    onChange: (value) => handleFilterChange("priceLt", value),
                    placeholder: t("back.management.menu.product.enterPrice"),
                    type: "input",
                  },
                  {
                    label: t("back.management.menu.product.productType"),
                    value: filterState.productType,
                    onChange: (value) =>
                      handleFilterChange("productType", value),
                    placeholder: t(
                      "back.management.menu.product.selectProductType",
                    ),
                    type: "select",
                    options: [
                      {
                        value: "Physical Product",
                        label: t(
                          "back.management.menu.product.physicalProduct",
                        ),
                      },
                      {
                        value: "Service",
                        label: t("back.management.menu.product.service"),
                      },
                    ],
                  },
                ]}
                onRemoveFilters={handleRemoveFilters}
              />
            </div>
          </div>

          <DataTable
            data={productItems}
            columns={columns}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalItems={totalItems}
            sortState={sortState}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            onEdit={(id) => navigate(`/menu/products/edit/${id}`)}
            onDelete={openDeleteConfirmation}
            getStatusColor={getStatusColor}
            renderMobileCard={renderMobileCard}
          />
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
};

export default Product;
